import { Component, Input, Output, EventEmitter, ViewChild, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { LeadReportMotivation, LeadReportMotivationLabels } from '../../../../models/lead.model';
import { PermissionGateService } from '../../../../../services/permission-gate.service';
import { requiredPermissionForConfirmation } from '../../../../utils/partner-permissions.util';

export interface ConfirmationModalOptions {
  title?: string;
  message?: string;
  submessage?: string;
  warningMessage?: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonClass?: string;
  icon?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  reportMode?: boolean;
  showPhoneShareCheckbox?: boolean;
  phoneShareMessage?: string;
  checkboxText?: string;
  includedServices?: string[];
  excludedServices?: string[];
  requirePhoneDigits?: boolean;
  cost?: number;
  freeAcceptance?: boolean;
}


@Component({
  selector: 'app-confirmation-modal',
  templateUrl: './confirmation-modal.component.html',
  styleUrls: ['./confirmation-modal.component.css'],
  standalone: false
})
export class ConfirmationModalComponent {
  @Input() title = '';
  @Input() message = '';
  @Input() submessage = '';
  @Input() warningMessage = '';
  @Input() cancelText = '';
  @Input() confirmText = '';
  @Input() confirmButtonClass = '';
  @Input() icon = '';
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() reportMode = false;
  @Input() showPhoneShareCheckbox = false;
  @Input() includedServices: string[] = [];
  @Input() excludedServices: string[] = [];
  @Input() phoneShareMessage = '';
  @Input() checkboxText = '';
  @Input() requirePhoneDigits = false;
  @Input() cost?: number;
  @Input() freeAcceptance?: boolean;

  @Output() confirmed = new EventEmitter<{ motivation?: LeadReportMotivation; text?: string; phoneDigits?: string }>();
  @Output() dismissed = new EventEmitter<void>();

  @ViewChild('modalTemplate', { static: true })
  private modalTemplateRef!: TemplateRef<any>;

  private modalRef?: NgbModalRef;

  motivations = Object.keys(LeadReportMotivation)
    .filter(key => isNaN(Number(key)))
    .map(key => LeadReportMotivation[key as keyof typeof LeadReportMotivation]);

  motivationsWithLabels = this.motivations.map(m => ({
    value: m,
    label: LeadReportMotivationLabels[m as keyof typeof LeadReportMotivationLabels]
  }));
  selectedMotivation?: LeadReportMotivation;
  motivationText = '';
  phoneShareAccepted = false;
  phoneDigits = '';

  constructor(private modalService: NgbModal, private permissionGate: PermissionGateService) { }

  open(options: ConfirmationModalOptions = {}): NgbModalRef | undefined {
    // Every paid/accept action of the partner panel confirms through here (phone unlocks pass
    // requirePhoneDigits, acceptances pass freeAcceptance): a single gate covers all ~10 entry points.
    // Non-partner users (admin, support, patients) always pass (see ProfileService.meets).
    const required = requiredPermissionForConfirmation(options);
    if (required && !this.permissionGate.ensure(required)) {
      return undefined;
    }
    this.title = options.title ?? 'Confirm Action';
    this.message = options.message ?? 'Are you sure?';
    this.submessage = options.submessage ?? '';
    this.warningMessage = options.warningMessage ?? '';
    this.confirmText = options.confirmText ?? 'Confirm';
    this.cancelText = options.cancelText ?? 'Cancel';
    this.confirmButtonClass = options.confirmButtonClass ?? 'btn-primary';
    this.icon = options.icon ?? '';
    this.size = options.size ?? 'md';

    this.reportMode = options.reportMode ?? false;
    this.selectedMotivation = undefined;
    this.motivationText = '';

    this.showPhoneShareCheckbox = options.showPhoneShareCheckbox ?? false;
    this.phoneShareMessage = options.phoneShareMessage ?? '';
    this.includedServices = options.includedServices ?? [];
    this.excludedServices = options.excludedServices ?? [];
    this.checkboxText = options.checkboxText ?? '';
    this.phoneShareAccepted = false;

    this.requirePhoneDigits = options.requirePhoneDigits ?? false;
    this.phoneDigits = '';
    this.cost = options.cost;
    this.freeAcceptance = options.freeAcceptance;

    this.modalRef = this.modalService.open(this.modalTemplateRef, {
      centered: false,
      size: this.size,
      backdrop: true,
    });

    this.modalRef.result
      .then(result => {
        if (result === 'confirm') {
          this.confirmed.emit({
            motivation: this.selectedMotivation,
            text: this.motivationText,
            phoneDigits: this.phoneDigits
          });
        }
      })
      .catch(() => this.dismissed.emit());


    return this.modalRef;
  }


  confirm(): void {
    if (this.reportMode) {
      if (!this.selectedMotivation || !this.motivationText.trim()) {
        return;
      }
    }
    if (this.showPhoneShareCheckbox && !this.phoneShareAccepted) {
      return;
    }
    if (this.requirePhoneDigits) {
      const digits = this.phoneDigits.trim();
      if (!digits || digits.length !== 3 || !/^\d{3}$/.test(digits)) {
        return;
      }
    }
    this.modalRef?.close('confirm');
  }

  dismiss(): void {
    this.modalRef?.dismiss();
  }

  get isFormValid(): boolean {
    if (this.reportMode) {
      return !!(this.selectedMotivation && this.motivationText.trim());
    }
    if (this.showPhoneShareCheckbox) {
      return this.phoneShareAccepted;
    }
    if (this.requirePhoneDigits) {
      const digits = this.phoneDigits.trim();
      return !!(digits && digits.length === 3 && /^\d{3}$/.test(digits));
    }
    return true;
  }

  isPhoneDigitsInvalid(): boolean {
    if (!this.phoneDigits) return false;
    const digits = this.phoneDigits.trim();
    return digits.length !== 3 || !/^\d{3}$/.test(digits);
  }
}
